import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  chakra,
  keyframes,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import Header from '../../../../components/Header/Header';
import { FaCity, FaEllipsisH, FaGraduationCap, FaIdCard, FaPhone, FaRoad, FaUser } from 'react-icons/fa';
import React, { useContext, useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  IoCalendarNumberSharp,
  IoEyeOffOutline,
  IoEyeOutline,
  IoHandLeft,
  IoInformationCircleSharp,
  IoLocationSharp,
  IoPhonePortraitOutline,
  IoSearchSharp,
} from 'react-icons/io5';
import { useRequestGetState } from '../../../../services/hooks/SignUp/useRequestGetState';
import { useRequestGetRaces } from '../../../../services/hooks/SignUp/useRequestGetRaces';
import { useRequestGetDocumentType } from '../../../../services/hooks/SignUp/useRequestGetDocumentType';
import { useRequestGetGender } from '../../../../services/hooks/SignUp/useRequestGetGender';
import { useRequestGetMaritalStatus } from '../../../../services/hooks/SignUp/useRequestGetMaritalStatus';
import { useRequestGetNationality } from '../../../../services/hooks/SignUp/useRequestGetNationality';
import { useRequestGetEducationLevel } from '../../../../services/hooks/SignUp/useRequestGetEducationLevel';
import { ContextAuth } from '../../../../contexts/Authentication';
import { EFormatDate, formatDate } from '../../../../utils/formatData';
import { PiIdentificationBadgeFill } from 'react-icons/pi';
import { GoNumber } from 'react-icons/go';
import { BsGenderAmbiguous } from 'react-icons/bs';
import { GiRing } from 'react-icons/gi';
import { LiaGlobeAmericasSolid } from 'react-icons/lia';
import { ETypeError, switchMessageError, validateDate } from '../../../../utils/validateDate';
import { IoIosMail } from 'react-icons/io';
import { BiSolidCity } from 'react-icons/bi';
import { useValidateCEP } from '../../../../services/hooks/SignUp/useCepValidation';
import { validateCPF } from '../../../../utils/validateCPF';
import InputMobile from '../../../../components/Input/Mobile';
import InputSelectMobile from '../../../../components/Input/Mobile/Select';
import { useRequestGetCity } from '../../../../services/hooks/SignUp/useRequestGetCity';
import { UseUpdateProfileData } from '../../../../services/hooks/Profile/usePostProfileData';
import CardHeaderMobile from '../../../../components/Mobile/header';
import NavigationButtonMobilePage from '../../../../components/Mobile/navigationButton';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCreateCandidate } from '../../../../services/hooks/SignUp/useCreateCandidate';
import { MdOutlinePassword } from 'react-icons/md';
import { useAuditLogger } from '../../../../services/hooks/Audit';
import { extractProfileChanges, formatChangesMessage } from '../../../../utils/auditHelpers';
import { useRequestGetGenderIdentity } from '../../../../services/hooks/SignUp/useRequestGetGenderIdentity';

const floatIn = keyframes`
      from { opacity: 0; transform: translateY(18px) scale(0.995); }
      
    `;
const AnimatedBox = chakra(Box, {
  baseStyle: {
    animation: `${floatIn} 420ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

const ProfileMobilePage: React.FC = () => {
  // Query Params
  const [searchParams] = useSearchParams();
  const route = searchParams?.get('route');
  const selectiveProcessId = searchParams?.get('selective_process_id');
  const isSignUp = useLocation().pathname.split('/')[1] === 'cadastro';
  const is_foreign_url = searchParams?.get('is_foreign') === 'true';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // State
  const [city, setCity] = useState<string>();
  const [firstChange, setFirstChange] = useState<boolean>(false);

  // context
  const { candidate, setCandidate, signIn } = useContext(ContextAuth);

  const bgColorAccordion = '#ececec';
  // const colorAccordion = '#838383';
  // const bgColorAccordion = bgColorAccordion;
  const colorAccordion = 'primaryColor';

  const validateCEP = useValidateCEP();

  // request data
  const getStates = useRequestGetState();
  const getRaces = useRequestGetRaces();
  const getDocumentType = useRequestGetDocumentType();
  const getGender = useRequestGetGender();
  const getGenderIdentity = useRequestGetGenderIdentity();
  const getMaritalStatus = useRequestGetMaritalStatus();
  const getNationality = useRequestGetNationality();
  const getEducationLevel = useRequestGetEducationLevel();

  const updateProfileData = UseUpdateProfileData();
  const createProfile = useCreateCandidate();
  const { logProfileAction } = useAuditLogger();

  const toast = useToast();

  const PersonalData = {
    is_foreign: Yup.boolean(),
    full_name: Yup.string()
      .trim()
      .required('Campo Obrigatorio')
      .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/, 'O nome não pode conter caracteres especiais')
      .test('nome-completo', 'Digite seu nome completo', (value) => {
        if (!value) return false; // garante que não seja vazio
        const parts = value.trim().split(/\s+/); // separa por espaços
        return parts.length >= 2; // precisa ter pelo menos duas partes
      }),
    mother_name: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) =>
        schema.required('Campo Obrigatorio').test('nome-completo-mae', 'Digite o nome completo', (value) => {
          if (!value) return false; // garante que não seja vazio
          const parts = value.trim().split(/\s+/); // separa por espaços
          return parts.length >= 2; // precisa ter pelo menos duas partes
        }),
    }),
    passport_number: Yup.string()
      .uppercase()
      .when('is_foreign', {
        is: true,
        then: (schema) => schema.required('Campo obrigatório'),
      }),
    cpf: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) =>
        schema
          .required('Campo obrigatório')
          .test('invalidCPF', 'CPF incompleto', (value) => {
            const cpf = value ?? '';
            const digits = cpf.replace(/\D/g, '');
            return digits.length >= 11;
          })
          .test('invalidCPF', 'Informe um CPF válido', (value) => {
            const cpf = value ?? '';
            const numbers = cpf.replace(/\D/g, '');
            if (numbers.length === 11) return validateCPF(numbers);
            else if (numbers.length > 11) return false;
            return true;
          }),
    }),
    date_of_birth: Yup.string().test('dataInvalid', (value, { createError, path }) => {
      const typeError = validateDate(value);

      if (typeError === ETypeError.noError) return true;

      return createError({
        message: switchMessageError(typeError),
        path: path,
      });
    }),
    education_level: Yup.string().required('Campo Obrigatorio'),
    gender: Yup.string().required('Campo Obrigatorio'),
    marital_status: Yup.string().required('Campo Obrigatorio'),
    race: Yup.string().required('Campo Obrigatorio'),

    nationality_foreign: Yup.string().when('nationality', {
      is: (nationality: string) => {
        const apiNationality = getNationality?.data?.find((get) => get.id === nationality);
        if (apiNationality && apiNationality.name.toLowerCase().includes('outro')) return true;
      },
      then: (schema) => schema.required('O campo é Obrigatorio'),
    }),

    document_type: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    document_number: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    document_issuing_body: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'), //Orgão emissor
    }),

    document_issuing_body_uf: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    document_date_of_issuance: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) =>
        schema.test('insuanceInvalid', (value, { createError, path }) => {
          const typeError = validateDate(value);

          if (typeError === ETypeError.noError) return true;

          return createError({
            message: switchMessageError(typeError),
            path: path,
          });
        }),
    }),

    nationality: Yup.string().required('Campo Obrigatorio'),

    state_naturality: Yup.string().when('nationality', {
      is: (nationality: string) => {
        const apiNationality = getNationality?.data?.find((get) => get.id === nationality);
        if (apiNationality && !apiNationality.name.toLowerCase().includes('outro')) return true;
      },
      then: (schema) => schema.required('O campo é Obrigatorio'),
    }),

    city_naturality: Yup.string().when('nationality', {
      is: (nationality: string) => {
        const apiNationality = getNationality?.data?.find((get) => get.id === nationality);
        if (apiNationality && !apiNationality.name.toLowerCase().includes('outro')) return true;
      },
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),
  };

  const AddresAndContact = {
    address_zip_code: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) =>
        schema.test('validateZip', 'Cep inválido', (cep) => {
          const zipCode = cep ?? '';
          const numbers = zipCode.replace(/\D/g, '');
          return !(numbers.length < 8);
        }),
    }),

    address_street: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    address_number: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    address_complement: Yup.string(),

    address_neighborhood: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    address_state: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    address_city: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    cellphone: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    phone: Yup.string(),

    nis: Yup.string(),
    email: Yup.string().required('Campo Obrigatorio').email('Preencha um e-mail válido').trim().lowercase(),
    confirm_email: Yup.string().when('email', {
      is: (email: string) => candidate?.user.email !== email,
      then: (schema) =>
        schema
          .required('Confirme o email')
          .trim()
          .lowercase()
          .oneOf([Yup.ref('email')], 'O E-Mail deve ser o mesmo digitado acima.'),
    }),
  };

  const dataAccess = {
    password: Yup.string().required('Campo Obrigatorio'),
    confirm_password: Yup.string()
      .required('Confirme a senha')
      .oneOf([Yup.ref('password')], 'A senha deve ser o mesmo digitado acima.'),
  };

  let validateShape = {
    ...PersonalData,
    ...AddresAndContact,
  };

  if (isSignUp) validateShape = { ...validateShape, ...dataAccess };

  const schema = Yup.object().shape(validateShape);

  const hookforms = useForm({
    resolver: yupResolver(schema),
    values: {
      nis: candidate?.nis || '',
      address_neighborhood: candidate?.address_neighborhood || '',
      address_number: candidate?.address_number || '',
      address_state: candidate?.address_state?.id || '',
      address_street: candidate?.address_street || '',
      document_issuing_body: candidate?.document_issuing_body || '',
      document_issuing_body_uf: candidate?.document_issuing_body_uf?.id || '',
      document_number: candidate?.document_number || '',
      document_type: candidate?.document_type?.id || '',
      education_level: candidate?.education_level?.id || '',
      full_name: candidate?.user?.full_name || '',
      gender: candidate?.gender?.id || '',
      marital_status: candidate?.marital_status?.id || '',
      mother_name: candidate?.mother_name || '',
      nationality: candidate?.nationality?.id || '',
      address_complement: candidate?.address_complement || '',
      address_zip_code: candidate?.address_zip_code || '',
      cellphone: candidate?.cellphone || '',
      cpf: candidate ? candidate?.cpf || '' : (!is_foreign_url && sessionStorage.getItem('document_number')) || '',
      passport_number: candidate
        ? candidate?.passport_number || ''
        : is_foreign_url
          ? sessionStorage.getItem('document_number') || ''
          : '',
      date_of_birth: formatDate(candidate?.date_of_birth.br || '', EFormatDate.US),
      document_date_of_issuance:
        candidate?.document_date_of_issuance?.br &&
        formatDate(candidate?.document_date_of_issuance?.br || '', EFormatDate.US),
      phone: candidate?.phone || '',
      state_naturality: candidate?.city_of_birth?.state?.id || '',
      city_naturality: candidate?.city_of_birth?.id || '',
      address_city: candidate?.address_city?.id || '',
      email: candidate?.user?.email || '',
      race: candidate?.race || '',
      is_foreign: candidate
        ? candidate.is_foreign
        : is_foreign_url && sessionStorage.getItem('document_number')
          ? true
          : false,
      nationality_foreign: candidate?.nationality_foreign || '',
    },
  });

  const getCity = useRequestGetCity(hookforms.watch('address_state'));
  const getNaturality = useRequestGetCity(hookforms.watch('state_naturality'));

  const accent = '#222';

  const navigate = useNavigate();

  useEffect(() => {
    if (!getCity.data) return;

    if (getCity.data.length > 0 && !firstChange && getNaturality.data && getNaturality.data?.length > 0) {
      hookforms.setValue('address_city', candidate?.address_city?.id || '');
      hookforms.setValue('city_naturality', candidate?.city_of_birth?.id);
      setFirstChange(true);
    }

    if (city) {
      const normalize = (str: string) =>
        str
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');

      const foundCity = getCity.data.find((localCity) => normalize(localCity.name) === normalize(city));

      if (foundCity) {
        hookforms.setValue('address_city', foundCity.id);
      }
    }
  }, [getCity.data, getNaturality.data, candidate, city, firstChange, hookforms.setValue]);

  const checkCep = async () => {
    const validationZipCode = await hookforms.trigger('address_zip_code');
    if (validationZipCode) {
      const cep = hookforms.getValues('address_zip_code');
      validateCEP.mutateAsync(cep).then((data) => {
        if (data?.erro) {
          toast({
            title: 'Erro',
            description: 'CEP não encontrado',
            status: 'error',
            duration: 4000,
            isClosable: true,
            position: 'top',
          });
        } else {
          getStates?.data?.forEach((state) => {
            if (state.initials == data?.uf) {
              hookforms.setValue('address_state', state.id || '');
            }
          });

          hookforms.setValue('address_street', data?.logradouro || '');

          hookforms.setValue('address_neighborhood', data?.bairro || '');

          setCity(data?.localidade);
        }
      });
    }
  };

  const handleCreateProfile = (data: FieldValues) => {
    if (data) {
      createProfile
        .mutateAsync(data)
        .then(() => {
          sessionStorage.removeItem('cpf');
          signIn(data?.email, data?.password).then(() => {
            toast({
              title: 'Sucesso',
              description: 'Cadastro realizado com sucesso',
              status: 'success',
              duration: 4000,
              isClosable: true,
            });
            if (route === '/processo-seletivo/inscricao') {
              navigate(route || '/painel/inscricoes', {
                state: { selectiveProcessId: selectiveProcessId },
              });
              return;
            }
            if (selectiveProcessId) {
              navigate(route || '/login', { state: selectiveProcessId });
            } else {
              navigate(route || '/login');
            }
          });
        })
        .catch((e) => {
          toast({
            title: 'Erro',
            description: e?.response?.data?.message || 'Erro na solicitação, tente novamente',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        });
    } else {
      toast({
        title: 'Erro',
        description: 'Dados invalidos, tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleProfileData = (data: FieldValues) => {
    if (candidate && Object.keys(data).length > 0) {
      updateProfileData
        .mutateAsync({ newData: data, oldData: candidate })
        .then((updated) => {
          setCandidate(updated);

          // ✅ Registrar auditoria das alterações
          const changes = extractProfileChanges(candidate, data);
          const changeCount = Object.keys(changes).length;

          if (changeCount > 0) {
            logProfileAction('profile_updated', formatChangesMessage(changes), 'info', changes);
          }

          toast({
            title: 'Sucesso',
            description: 'Dados atualizados com sucesso!',
            status: 'success',
            duration: 4000,
            isClosable: true,
            position: 'top',
          });
        })
        .catch((error) => {
          toast({
            title: (error?.message == 'Altere alguma informação!' && 'Atenção') || 'Erro',
            description:
              (error?.message == 'Altere alguma informação!' && error?.message) ||
              error?.response?.data?.message ||
              'Erro ao solicitar a atualização dos dados!',
            status: (error?.message == 'Altere alguma informação!' && 'warning') || 'error',
            duration: 4000,
            position: 'top',
            isClosable: true,
          });
        });
    }
  };

  const isBrazilian = getNationality.data
    ?.find((item) => item.id === hookforms.watch('nationality'))
    ?.name.toLowerCase()
    .includes('brasil');

  const loading =
    getGender.isLoading ||
    getMaritalStatus.isLoading ||
    getDocumentType.isLoading ||
    getEducationLevel.isLoading ||
    getNationality.isLoading;

  const verifyIsForeign = () => {
    return hookforms.watch('is_foreign');
  };

  const getOtherNationality = (): boolean => {
    const nationality_id = hookforms.watch('nationality');
    let response = false;
    getNationality.data?.forEach((nationality) => {
      if (nationality.id == nationality_id) {
        response = nationality.name.toLowerCase().includes('outro');
        return;
      }
    });

    return response;
  };

  const is_foreign = hookforms.watch('is_foreign');

  return (
    <Box h="100dvh" as="form" onSubmit={hookforms.handleSubmit(isSignUp ? handleCreateProfile : handleProfileData)}>
      {validateCEP.isPending && (
        <Modal isOpen={true} onClose={() => {}} isCentered size="md">
          <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />
          <ModalContent bgColor="transparent" color="white" w="60%" borderRadius="xl" p={2}>
            <Box
              bgGradient="linear(to-b, gray.900, gray.800)"
              position="absolute"
              borderRadius="xl"
              left={0}
              top={0}
              opacity={0.8}
              w="full"
              h="full"
            />
            <ModalBody zIndex={9}>
              <VStack spacing={3} my={2}>
                <Spinner thickness="4px" speed="0.6s" emptyColor="gray.600" color="blue.400" size="xl" />
                <Text fontSize="md" color="gray.300" textAlign="center">
                  Buscando o CEP...
                </Text>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
      <Stack height="calc(100% - 30px)" w="100%" top={0} bg="white" gap={0}>
        <Header />
        <VStack mt={0} flex={1} overflowY="auto" py={2} px={3}>
          {(loading && (
            <VStack flex={1} w="100%" justify="center">
              <Spinner emptyColor="gray.100" color="blue.500" size="xl" />
              <Text fontSize="sm">Buscando dados...</Text>
            </VStack>
          )) || (
            <AnimatedBox
              bgGradient={useColorModeValue('linear(to-br, gray.50, white)', 'linear(to-br, gray.900, gray.800)')}
              borderRadius="lg"
              boxShadow="lg"
              p={{ base: 5, md: 10 }}
              borderWidth={'1px'}
              borderColor={useColorModeValue('gray.100', 'gray.700')}
              bg="white"
              w="100%"
            >
              <FormProvider {...hookforms}>
                <VStack boxSizing="border-box" maxW="lg" spacing={4} align="stretch">
                  <CardHeaderMobile
                    title={isSignUp ? 'Dados cadastrais' : 'Atualização de dados cadastrais'}
                    subTitle="A alteração de senha não está disponível neste formulário. Para realizar a alteração, acesse o menu e selecione a opção Alterar Senha."
                    badgeText={isSignUp ? 'cadastro' : 'Perfil'}
                    badgeIcon={<FaUser size={12} />}
                    colorScheme="purple"
                    px={3}
                  />
                  <VStack spacing={4} align="stretch">
                    <Accordion defaultIndex={[0, 1, 2]} allowMultiple>
                      <AccordionItem border="none" borderRadius="md" mb={3} shadow="md" overflow="hidden">
                        <h2>
                          <AccordionButton
                            bg={bgColorAccordion}
                            color={colorAccordion}
                            transition="0.3s"
                            _expanded={{ bg: bgColorAccordion, color: { colorAccordion }, py: 3, transition: '0.3s' }}
                          >
                            <Box flex="1" textAlign="left" fontWeight="bold">
                              Dados gerais
                            </Box>
                            <AccordionIcon color="black" />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel p={5}>
                          <Stack spacing={4}>
                            <InputMobile
                              name={is_foreign ? 'passport_number' : 'cpf'}
                              placeholderLabel={is_foreign ? 'Passaporte' : 'CPF'}
                              {...(isSignUp
                                ? undefined
                                : {
                                    tooltip: `O ${is_foreign ? 'Passaporte' : 'CPF'} não pode ser alterado`,
                                    iconRight: <IoInformationCircleSharp size={16} color="#0780f1" />,
                                  })}
                              iconLeft={<FaIdCard size={12} color={accent} />}
                              isDisabled={isSignUp ? false : true}
                            />
                            <InputMobile
                              name="full_name"
                              placeholderLabel="Nome completo"
                              iconLeft={<FaUser size={12} color={accent} />}
                              isRequired
                            />
                            <InputMobile
                              name="date_of_birth"
                              placeholderLabel="Data de nascimento"
                              type="date"
                              iconLeft={<IoCalendarNumberSharp size={12} color={accent} />}
                              isRequired
                            />
                            <InputMobile
                              name="mother_name"
                              placeholderLabel="Nome da mãe"
                              iconLeft={<FaUser size={12} color={accent} />}
                              isRequired={!verifyIsForeign()}
                            />
                            {!verifyIsForeign() && (
                              <>
                                <InputSelectMobile
                                  name="document_type"
                                  placeholderLabel="Tipo de Documento"
                                  iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
                                  isRequired
                                  listItens={getDocumentType.data}
                                />
                                <InputMobile
                                  name="document_number"
                                  placeholderLabel="Número do Documento"
                                  iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
                                  isRequired
                                  onlyNumber
                                />
                                <InputMobile
                                  name="document_date_of_issuance"
                                  placeholderLabel="Data de expedição"
                                  type="date"
                                  iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
                                  isRequired
                                />
                                <InputMobile
                                  name="document_issuing_body"
                                  placeholderLabel="Órgão"
                                  iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
                                  isRequired
                                />
                                <InputSelectMobile
                                  name="document_issuing_body_uf"
                                  placeholderLabel="UF"
                                  iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
                                  isRequired
                                  listItens={getStates.data}
                                />
                              </>
                            )}

                            {!isSignUp && !candidate?.is_foreign && (
                              <InputMobile
                                name="nis"
                                placeholderLabel="Informe seu NIS"
                                iconLeft={<GoNumber size={15} color={accent} />}
                                onlyNumber
                              />
                            )}
                            <InputSelectMobile
                              name="education_level"
                              placeholderLabel="Escolaridade"
                              iconLeft={<FaGraduationCap size={15} color={accent} />}
                              isRequired
                              listItens={getEducationLevel.data}
                            />
                            <InputSelectMobile
                              name="gender"
                              placeholderLabel="Sexo"
                              iconLeft={<BsGenderAmbiguous size={15} color={accent} />}
                              isRequired
                              listItens={getGender.data}
                            />
                            <InputSelectMobile
                              name="gender_identity"
                              placeholderLabel="Identidade de gênero"
                              iconLeft={<BsGenderAmbiguous size={15} color={accent} />}
                              isRequired
                              listItens={getGenderIdentity.data}
                            />
                            <InputSelectMobile
                              name="marital_status"
                              placeholderLabel="Estado cívil"
                              iconLeft={<GiRing size={15} color={accent} />}
                              isRequired
                              listItens={getMaritalStatus.data}
                            />
                            <InputSelectMobile
                              name="race"
                              placeholderLabel="Raça"
                              iconLeft={<IoHandLeft size={15} color={accent} />}
                              isRequired
                              listItens={getRaces.data}
                            />
                            <InputSelectMobile
                              name="nationality"
                              placeholderLabel="Nacionalidade"
                              iconLeft={<LiaGlobeAmericasSolid size={15} color={accent} />}
                              isRequired
                              listItens={getNationality.data}
                            />
                            {isBrazilian && (
                              <>
                                <InputSelectMobile
                                  name="state_naturality"
                                  placeholderLabel="Naturalidade (estado)"
                                  iconLeft={<FaCity size={15} color={accent} />}
                                  isRequired
                                  listItens={getStates.data}
                                />
                                <InputSelectMobile
                                  name="city_naturality"
                                  placeholderLabel="Naturalidade (cidade)"
                                  iconLeft={<FaCity size={15} color={accent} />}
                                  isRequired
                                  listItens={getNaturality.data}
                                />
                              </>
                            )}

                            {getOtherNationality() && (
                              <InputMobile
                                name="nationality_foreign"
                                placeholderLabel="Informe sua nacionalidade"
                                iconLeft={<PiIdentificationBadgeFill size={15} color={accent} />}
                                isRequired
                              />
                            )}
                          </Stack>
                        </AccordionPanel>
                      </AccordionItem>
                      <AccordionItem border="none" borderRadius="md" mb={3} shadow="md" overflow="hidden">
                        <h2>
                          <AccordionButton
                            bg={bgColorAccordion}
                            color={colorAccordion}
                            transition="0.3s"
                            _expanded={{ bg: bgColorAccordion, color: colorAccordion, py: 3, transition: '0.3s' }}
                          >
                            <Box flex="1" textAlign="left" fontWeight="bold">
                              Endereço e contatos
                            </Box>
                            <AccordionIcon color="black" />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel p={5}>
                          <Stack spacing={4}>
                            <InputMobile
                              name="address_zip_code"
                              placeholderLabel="CEP"
                              iconLeft={<IoLocationSharp size={15} color={accent} />}
                              iconRight={<IoSearchSharp size={16} color="#0780f1" />}
                              onClickRightIcon={checkCep}
                              mask="99999-999"
                              isRequired={!verifyIsForeign()}
                            />
                            <InputMobile
                              name="address_street"
                              placeholderLabel="Endereço"
                              iconLeft={<FaRoad size={13} color={accent} />}
                              isRequired={!verifyIsForeign()}
                            />
                            <InputMobile
                              name="address_number"
                              placeholderLabel="Número"
                              iconLeft={<GoNumber size={16} color={accent} />}
                              onlyNumber
                              isRequired={!verifyIsForeign()}
                            />
                            <InputMobile
                              name="address_complement"
                              placeholderLabel="Complemento"
                              iconLeft={<FaEllipsisH size={14} color={accent} />}
                            />
                            <InputMobile
                              name="address_neighborhood"
                              placeholderLabel="Bairro"
                              iconLeft={<BiSolidCity size={14} color={accent} />}
                              isRequired={!verifyIsForeign()}
                            />
                            <InputSelectMobile
                              name="address_state"
                              placeholderLabel="UF"
                              iconLeft={<FaCity size={14} color={accent} />}
                              isRequired={!verifyIsForeign()}
                              listItens={getStates.data}
                            />
                            <InputSelectMobile
                              name="address_city"
                              placeholderLabel="Cidade"
                              iconLeft={<FaCity size={14} color={accent} />}
                              isRequired={!verifyIsForeign()}
                              listItens={getCity.data}
                            />
                            <InputMobile
                              name="phone"
                              placeholderLabel="Telefone"
                              iconLeft={<FaPhone size={12} color={accent} />}
                              value={hookforms.watch('phone')}
                              mask="(99) 9999-9999"
                            />
                            <InputMobile
                              name="cellphone"
                              placeholderLabel="Celular"
                              iconLeft={<IoPhonePortraitOutline size={14} color={accent} />}
                              value={hookforms.watch('cellphone')}
                              isRequired={!verifyIsForeign()}
                              mask="(99) 99999-9999"
                            />
                            {!isSignUp && (
                              <>
                                <InputMobile
                                  name="email"
                                  placeholderLabel="E-Mail"
                                  iconLeft={<IoIosMail size={15} color={accent} />}
                                  isRequired
                                />
                                {hookforms.watch('email') !== candidate?.user.email && (
                                  <InputMobile
                                    name="confirm_email"
                                    placeholderLabel="Confirme o E-Mail"
                                    iconLeft={<IoIosMail size={15} color={accent} />}
                                    isRequired
                                  />
                                )}
                              </>
                            )}
                          </Stack>
                        </AccordionPanel>
                      </AccordionItem>
                      {isSignUp && (
                        <AccordionItem border="none" borderRadius="md" mb={3} shadow="md" overflow="hidden">
                          <h2>
                            <AccordionButton
                              bg={bgColorAccordion}
                              color={colorAccordion}
                              transition="0.3s"
                              _expanded={{ bg: bgColorAccordion, color: colorAccordion, py: 3, transition: '0.3s' }}
                            >
                              <Box flex="1" textAlign="left" fontWeight="bold">
                                Dados de acesso
                              </Box>
                              <AccordionIcon color="black" />
                            </AccordionButton>
                          </h2>
                          <AccordionPanel p={5}>
                            <Stack spacing={4}>
                              <InputMobile
                                name="email"
                                placeholderLabel="E-Mail"
                                iconLeft={<IoIosMail size={15} color={accent} />}
                                isRequired
                              />
                              <InputMobile
                                name="confirm_email"
                                placeholderLabel="Confirme o E-Mail"
                                iconLeft={<IoIosMail size={15} color={accent} />}
                                isRequired
                              />
                              <InputMobile
                                name="password"
                                placeholderLabel="Senha"
                                iconLeft={<MdOutlinePassword size={15} color={accent} />}
                                type={showPassword ? 'text' : 'password'}
                                iconRight={
                                  <button onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <IoEyeOutline size={16} /> : <IoEyeOffOutline size={16} />}
                                  </button>
                                }
                                isRequired
                              />

                              <InputMobile
                                name="confirm_password"
                                placeholderLabel="Confirme a senha"
                                iconLeft={<MdOutlinePassword size={15} color={accent} />}
                                type={showConfirmPassword ? 'text' : 'password'}
                                iconRight={
                                  <button onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                    {showConfirmPassword ? <IoEyeOutline size={16} /> : <IoEyeOffOutline size={16} />}
                                  </button>
                                }
                                isRequired
                              />
                            </Stack>
                          </AccordionPanel>
                        </AccordionItem>
                      )}
                    </Accordion>
                  </VStack>
                </VStack>
              </FormProvider>
            </AnimatedBox>
          )}
        </VStack>
      </Stack>
      <NavigationButtonMobilePage
        absolute
        mainButtonText={isSignUp ? 'Cadastrar-se' : 'Salvar dados'}
        typeButton="submit"
        isLoading={updateProfileData.isPending || createProfile.isPending}
        w="100%"
      />
    </Box>
  );
};

export default ProfileMobilePage;
